import { Alert } from '@mui/material';
import {
  DetailedHTMLProps,
  forwardRef,
  ImgHTMLAttributes,
  ReactEventHandler,
} from 'react';
import { useBoolean } from 'usehooks-ts';

import { Icon } from '@/components/Icon/Icon';
import { Loader } from '@/components/Icon/Loader';

import { ImageContainer, ImageLoader, StyledImage } from './File.styled';

type ImageProps = DetailedHTMLProps<
  ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  hideLoader?: boolean;
};

export const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(
  { src, alt, onLoad, onError, hideLoader, ...props }: ImageProps,
  ref,
) {
  const { value: isLoaded, setTrue: setLoaded } = useBoolean(false);
  const { value: isError, setTrue: setError } = useBoolean(false);

  const handleLoad: ReactEventHandler<HTMLImageElement> = event => {
    setLoaded();
    onLoad?.(event);
  };

  const handleError: ReactEventHandler<HTMLImageElement> = event => {
    setError();
    setLoaded();
    onError?.(event);
  };

  return (
    <ImageContainer>
      {isError ? (
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          Failed to load image.
        </Alert>
      ) : (
        <StyledImage
          ref={ref}
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          isHidden={!isLoaded}
          {...props}
        />
      )}
      {!hideLoader && !isLoaded && (
        <ImageLoader>
          <Loader />
        </ImageLoader>
      )}
    </ImageContainer>
  );
});
